/**
 * O que faz: Coleta um único input por vez para iniciar o diagnóstico SEO de Perfil.
 * Onde usa: SeoProfileAnalyzerPage e SeoProfileOnboardingStep.
 * Conexões: onSubmit recebido por props.
 * Tabelas: Não se aplica.
 * Data: 14/05/2026
 */

import type { FormEvent } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Globe2, Instagram, Loader2, MapPin, Search, Stethoscope } from 'lucide-react';
import type { SeoProfileAnalysisSource, SeoProfileRequest } from '@/types/seoProfile';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface SeoProfileFormProps {
  analyzing: boolean;
  compact?: boolean;
  onSubmit: (payload: Omit<SeoProfileRequest, 'clinica_id'>) => Promise<unknown>;
}

type SourceConfig = {
  label: string;
  icon: React.ReactNode;
  placeholder: string;
  inputLabel: string;
};

const sources: Record<SeoProfileAnalysisSource, SourceConfig> = {
  google_business: {
    label: 'Google',
    icon: <MapPin className="h-4 w-4" />,
    placeholder: 'Link do perfil no Google Maps',
    inputLabel: 'Google Meu Negócio',
  },
  website: {
    label: 'Site',
    icon: <Globe2 className="h-4 w-4" />,
    placeholder: 'https://sua-clinica.com.br',
    inputLabel: 'Site ou landing page',
  },
  instagram: {
    label: 'Instagram',
    icon: <Instagram className="h-4 w-4" />,
    placeholder: 'https://instagram.com/sua_clinica',
    inputLabel: 'Instagram',
  },
  doctoralia: {
    label: 'Doctoralia',
    icon: <Stethoscope className="h-4 w-4" />,
    placeholder: 'Link do perfil na Doctoralia',
    inputLabel: 'Doctoralia',
  },
  general_search: {
    label: 'Busca',
    icon: <Search className="h-4 w-4" />,
    placeholder: 'Clínica Aura dermatologia Curitiba',
    inputLabel: 'Nome, especialidade e cidade',
  },
};

const ALL_SOURCES: SeoProfileAnalysisSource[] = [
  'google_business',
  'website',
  'instagram',
  'doctoralia',
  'general_search',
];

const COMPACT_SOURCES: SeoProfileAnalysisSource[] = [
  'google_business',
  'website',
  'instagram',
  'general_search',
];

export function SeoProfileForm({ analyzing, compact = false, onSubmit }: SeoProfileFormProps) {
  const [analysisSource, setAnalysisSource] = useState<SeoProfileAnalysisSource>('google_business');
  const [values, setValues] = useState<Record<SeoProfileAnalysisSource, string>>({
    google_business: '',
    website: '',
    instagram: '',
    doctoralia: '',
    general_search: '',
  });

  const visibleSources = compact ? COMPACT_SOURCES : ALL_SOURCES;
  const currentSource = sources[analysisSource];

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!values[analysisSource].trim()) {
      toast.error(`Informe os dados de ${currentSource.inputLabel} para analisar.`);
      return;
    }

    const payloadBySource: Record<SeoProfileAnalysisSource, Omit<SeoProfileRequest, 'clinica_id'>> = {
      google_business: {
        input_mode: 'links',
        analysis_source: 'google_business',
        google_business_url: values.google_business.trim(),
      },
      website: {
        input_mode: 'links',
        analysis_source: 'website',
        website_url: values.website.trim(),
      },
      instagram: {
        input_mode: 'links',
        analysis_source: 'instagram',
        instagram_url: values.instagram.trim(),
      },
      doctoralia: {
        input_mode: 'links',
        analysis_source: 'doctoralia',
        doctoralia_url: values.doctoralia.trim(),
      },
      general_search: {
        input_mode: 'general_search',
        analysis_source: 'general_search',
        search_query: values.general_search.trim(),
      },
    };

    await onSubmit(payloadBySource[analysisSource]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Seletor de fonte — grid 2 colunas com checkmark */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground">Canal para análise</Label>
        <div className="grid grid-cols-2 gap-2">
          {visibleSources.map((source) => {
            const cfg = sources[source];
            const isActive = analysisSource === source;
            const isLastOdd =
              visibleSources.length % 2 !== 0 &&
              source === visibleSources[visibleSources.length - 1];

            return (
              <button
                key={source}
                type="button"
                onClick={() => setAnalysisSource(source)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-all duration-150',
                  isActive
                    ? 'border-emerald-700 bg-emerald-700 text-white shadow-sm'
                    : 'border-border bg-background text-muted-foreground hover:border-emerald-300 hover:text-foreground',
                  isLastOdd ? 'col-span-2' : '',
                )}
              >
                <span className={cn('shrink-0', isActive ? 'text-white/80' : 'text-muted-foreground')}>
                  {cfg.icon}
                </span>
                <span className="flex-1 text-left">{cfg.label}</span>
                {isActive && (
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white">
                    <Check className="h-2.5 w-2.5 text-emerald-700" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Input com ícone da fonte à esquerda */}
      <div className="space-y-1.5">
        <Label htmlFor="source-input">{currentSource.inputLabel}</Label>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
            {currentSource.icon}
          </span>
          <Input
            id="source-input"
            value={values[analysisSource]}
            onChange={(e) => setValues((prev) => ({ ...prev, [analysisSource]: e.target.value }))}
            placeholder={currentSource.placeholder}
            disabled={analyzing}
            className="pl-9"
          />
        </div>
      </div>

      <Button type="submit" disabled={analyzing} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700">
        {analyzing ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Search className="h-4 w-4" />
        )}
        {analyzing ? 'Analisando...' : `Analisar ${currentSource.inputLabel}`}
      </Button>
    </form>
  );
}
